/**
 * zzai-pledge.js
 * Kickstarter backer pledge portal logic.
 * Rules (enforced strictly, no exceptions):
 *   - Selection unit is whole 12-packs only
 *   - 5 options: Mixed, Chapman, Zobo, TamTam, Obi
 *   - Sum must equal customer's total_packs entitlement exactly
 *   - Confirm disabled until sum === total_packs
 *   - Reward shirts: one size slot per `shirtEntitlementCount` (from metafield JSON)
 *   - Add-on shirts: optional paid extras (same tee variants), any count
 *   - Pack upsells are separate — do NOT affect entitlement count
 *   - Variant IDs must match server-rendered allowlist (mitigates tampering)
 *   - Cart line properties tag lines for the checkout validation Function
 */

(function () {
  'use strict';

  const MAX_PACKS = 500;
  const MAX_SHIRT_SLOTS = 20;
  const MAX_ADDON_SHIRTS = 30;
  const MAX_UPSELL_QTY_PER_VARIANT = 20;

  if (new URLSearchParams(window.location.search).has('preview')) {
    const loginState = document.querySelector('.pledge-state');
    const preview = document.getElementById('pledge-portal-preview');
    if (loginState) loginState.style.display = 'none';
    if (preview) {
      preview.style.display = 'block';
      if (!document.getElementById('pledge-portal')) {
        preview.id = 'pledge-portal';
      }
    }
  }

  const portal = document.getElementById('pledge-portal');
  if (!portal) return;

  const pledgeStatus = String(portal.dataset.pledgeStatus || '').trim();
  if (pledgeStatus !== 'pending') {
    return;
  }

  const totalPacksRaw = parseInt(portal.dataset.totalPacks, 10);
  const totalPacks = Number.isFinite(totalPacksRaw)
    ? Math.min(Math.max(0, totalPacksRaw), MAX_PACKS)
    : 0;
  const supportContact = portal.dataset.supportEmail || '';

  const VARIANT_IDS = {
    mixed: String(portal.dataset.variantMixed || '').trim(),
    chapman: String(portal.dataset.variantChapman || '').trim(),
    zobo: String(portal.dataset.variantZobo || '').trim(),
    tamtam: String(portal.dataset.variantTamtam || '').trim(),
    obi: String(portal.dataset.variantObi || '').trim(),
  };

  const SHIRT_VARIANT_IDS = {
    S: String(portal.dataset.variantTeeS || '').trim(),
    M: String(portal.dataset.variantTeeM || '').trim(),
    L: String(portal.dataset.variantTeeL || '').trim(),
    XL: String(portal.dataset.variantTeeXl || '').trim(),
    XXL: String(portal.dataset.variantTeeXxl || '').trim(),
  };

  const ADDON_SHIRT_VARIANT_IDS = {
    S: String(portal.dataset.variantTeeAddonS || '').trim(),
    M: String(portal.dataset.variantTeeAddonM || '').trim(),
    L: String(portal.dataset.variantTeeAddonL || '').trim(),
    XL: String(portal.dataset.variantTeeAddonXl || '').trim(),
    XXL: String(portal.dataset.variantTeeAddonXxl || '').trim(),
  };

  function readEntitlement() {
    const el =
      document.getElementById('zzai-pledge-entitlement') ||
      document.getElementById('zzai-pledge-entitlement-preview');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  const ent = readEntitlement();
  if (!ent) return;

  const discountCode = String(ent.discountCode || '').trim();

  const entPacks = Number(ent.totalPacks);
  if (!Number.isFinite(entPacks) || entPacks !== totalPacks) {
    console.warn('[zzai-pledge] entitlement JSON vs data-total-packs mismatch');
    return;
  }

  let shirtEntitlementCount = Number(ent.shirtEntitlementCount);
  if (!Number.isFinite(shirtEntitlementCount) || shirtEntitlementCount < 0) {
    shirtEntitlementCount = ent.includesShirt ? 1 : 0;
  }
  shirtEntitlementCount = Math.min(Math.max(0, Math.floor(shirtEntitlementCount)), MAX_SHIRT_SLOTS);

  const dataShirtCount = parseInt(portal.dataset.shirtEntitlementCount, 10);
  if (Number.isFinite(dataShirtCount) && dataShirtCount !== shirtEntitlementCount) {
    console.warn('[zzai-pledge] shirt count JSON vs data-shirt-entitlement-count mismatch; using JSON');
  }

  const allowPack = new Set(
    (Array.isArray(ent.allowedPackVariantIds) ? ent.allowedPackVariantIds : [])
      .map((v) => String(v == null ? '' : v).trim())
      .filter((v) => /^\d+$/.test(v)),
  );
  const allowUpsell = new Set(
    (Array.isArray(ent.upsellVariantIds) ? ent.upsellVariantIds : [])
      .map((v) => String(v == null ? '' : v).trim())
      .filter((v) => /^\d+$/.test(v)),
  );

  const shirtMap = ent.shirtVariantIds && typeof ent.shirtVariantIds === 'object' ? ent.shirtVariantIds : {};
  const allowShirt = new Set(
    Object.values(shirtMap)
      .map((v) => String(v == null ? '' : v).trim())
      .filter((v) => /^\d+$/.test(v)),
  );

  const addonShirtMap =
    ent.addonShirtVariantIds && typeof ent.addonShirtVariantIds === 'object'
      ? ent.addonShirtVariantIds
      : {};
  const addonShirtPrices =
    ent.addonShirtPrices && typeof ent.addonShirtPrices === 'object'
      ? ent.addonShirtPrices
      : {};
  const allowAddonShirt = new Set(
    Object.values(addonShirtMap)
      .map((v) => String(v == null ? '' : v).trim())
      .filter((v) => /^\d+$/.test(v)),
  );

  const swagLineKeys = Array.isArray(ent.swagLineKeys) ? ent.swagLineKeys : [];
  const swagVariantByKey =
    ent.swagVariantByKey && typeof ent.swagVariantByKey === 'object' ? ent.swagVariantByKey : {};
  const allowSwag = new Set(
    swagLineKeys
      .map((k) => String(swagVariantByKey[k] == null ? '' : swagVariantByKey[k]).trim())
      .filter((v) => /^\d+$/.test(v)),
  );

  function variantAllowedForPack(id) {
    const s = String(id || '').trim();
    return s && allowPack.has(s);
  }

  const state = { mixed: 0, chapman: 0, zobo: 0, tamtam: 0, obi: 0 };
  /** @type {Record<string, number>} */
  const upsells = {};
  /** @type {{ variantId: string, size: string }[]} */
  const shirtAddons = [];

  function addonShirtPrice(size) {
    return String(addonShirtPrices[size] || '').trim();
  }

  function updateAddonShirtPrice() {
    const priceEl = portal.querySelector('.js-shirt-addon-price');
    const sizeEl = portal.querySelector('.js-shirt-addon-size');
    if (!priceEl) return;
    const selectedSize = String(sizeEl?.value || '').trim();
    const selectedPrice = addonShirtPrice(selectedSize);
    const fallbackPrice =
      selectedPrice ||
      addonShirtPrice('M') ||
      addonShirtPrice('L') ||
      addonShirtPrice('S') ||
      addonShirtPrice('XL') ||
      addonShirtPrice('XXL');
    priceEl.textContent = fallbackPrice
      ? selectedSize
        ? `Shirt price: ${fallbackPrice}`
        : `Shirt price: ${fallbackPrice}`
      : '';
    priceEl.hidden = !fallbackPrice;
  }

  function selectedPacks() {
    return Object.values(state).reduce((a, b) => a + b, 0);
  }

  function shirtSlotsFilled() {
    if (shirtEntitlementCount <= 0) return true;
    for (let slot = 1; slot <= shirtEntitlementCount; slot++) {
      if (!portal.querySelector(`input[name="shirt_size_slot_${slot}"]:checked`)) {
        return false;
      }
    }
    return true;
  }

  function swagSkusReady() {
    if (!swagLineKeys.length) return true;
    return swagLineKeys.every((k) => {
      const id = String(swagVariantByKey[k] == null ? '' : swagVariantByKey[k]).trim();
      return id && /^\d+$/.test(id);
    });
  }

  function isValid() {
    return selectedPacks() === totalPacks && shirtSlotsFilled() && swagSkusReady();
  }

  function increment(sku) {
    if (!Object.prototype.hasOwnProperty.call(state, sku)) return;
    if (selectedPacks() >= totalPacks) return;
    state[sku]++;
    updateUI();
  }

  function adjustPacks(sku, delta) {
    if (!Object.prototype.hasOwnProperty.call(state, sku)) return;
    const amount = Math.trunc(Number(delta) || 0);
    if (!amount) return;
    const selected = selectedPacks();
    if (amount > 0) {
      state[sku] += Math.min(amount, Math.max(0, totalPacks - selected));
    } else {
      state[sku] = Math.max(0, state[sku] + amount);
    }
    updateUI();
  }

  function decrement(sku) {
    if (!Object.prototype.hasOwnProperty.call(state, sku)) return;
    if (state[sku] <= 0) return;
    state[sku]--;
    updateUI();
  }

  function renderShirtAddonList() {
    const ul = portal.querySelector('.js-shirt-addon-list');
    if (!ul) return;
    ul.innerHTML = '';
    if (shirtAddons.length === 0) {
      ul.hidden = true;
      return;
    }
    ul.hidden = false;
    const grouped = shirtAddons.reduce((acc, row) => {
      const size = String(row.size || '').trim();
      if (!size) return acc;
      acc[size] = acc[size] || { size, variantId: row.variantId, qty: 0 };
      acc[size].qty += 1;
      return acc;
    }, {});
    Object.values(grouped).forEach((row) => {
      const li = document.createElement('li');
      const price = addonShirtPrice(row.size);
      li.textContent = `Add-on shirt - size ${row.size} x ${row.qty}${price ? ` - ${price} each` : ''}`;
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'btn-zzai';
      rm.style.marginLeft = '12px';
      rm.style.fontSize = '0.85rem';
      rm.style.padding = '4px 10px';
      rm.textContent = 'Remove one';
      rm.addEventListener('click', () => {
        removeAddonShirtSize(row.size);
        renderShirtAddonList();
        updateUI();
      });
      li.appendChild(rm);
      ul.appendChild(li);
    });
  }

  function addonShirtCount(size) {
    const wanted = String(size || '').trim();
    if (!wanted) return 0;
    return shirtAddons.reduce((sum, row) => sum + (String(row.size || '').trim() === wanted ? 1 : 0), 0);
  }

  function removeAddonShirtSize(size) {
    const wanted = String(size || '').trim();
    if (!wanted) return false;
    const idx = shirtAddons.findLastIndex
      ? shirtAddons.findLastIndex((row) => String(row.size || '').trim() === wanted)
      : (() => {
          for (let i = shirtAddons.length - 1; i >= 0; i -= 1) {
            if (String(shirtAddons[i].size || '').trim() === wanted) return i;
          }
          return -1;
        })();
    if (idx < 0) return false;
    shirtAddons.splice(idx, 1);
    return true;
  }

  function addAddonShirtSize(size) {
    const selectedSize = String(size || '').trim();
    if (!selectedSize) {
      alert('Choose a size for the add-on shirt.');
      return false;
    }
    if (shirtAddons.length >= MAX_ADDON_SHIRTS) {
      alert('Too many add-on shirts in this order. Contact support if you need more.');
      return false;
    }
    const vid = String(ADDON_SHIRT_VARIANT_IDS[selectedSize] || '').trim();
    if (!vid || !allowAddonShirt.has(vid)) {
      alert('That shirt size is not available. Refresh the page or contact support.');
      return false;
    }
    shirtAddons.push({ variantId: vid, size: selectedSize });
    return true;
  }

  function setUpsellQty(variantId, qty) {
    const vid = String(variantId || '').trim();
    if (!vid || !allowUpsell.has(vid) || allowShirt.has(vid)) return;
    const next = Math.max(0, Math.min(MAX_UPSELL_QTY_PER_VARIANT, Number(qty) || 0));
    if (next <= 0) {
      delete upsells[vid];
      return;
    }
    upsells[vid] = next;
  }

  function updateUI() {
    const selected = selectedPacks();
    const full = selected >= totalPacks;

    Object.keys(state).forEach((sku) => {
      const countEl = portal.querySelector('#count-' + sku);
      if (countEl) countEl.textContent = state[sku];

      const tile = portal.querySelector(`.pack-tile[data-sku="${sku}"]`);
      if (tile) tile.classList.toggle('has-qty', state[sku] > 0);

      const minusBtn = portal.querySelector(`.pack-minus[data-sku="${sku}"]`);
      const plusBtn = portal.querySelector(`.pack-plus[data-sku="${sku}"]`);
      const bulkMinusBtn = portal.querySelector(`.pack-bulk-minus[data-sku="${sku}"]`);
      const bulkPlusBtn = portal.querySelector(`.pack-bulk-plus[data-sku="${sku}"]`);
      if (minusBtn) minusBtn.disabled = state[sku] === 0;
      if (plusBtn) plusBtn.disabled = full;
      if (bulkMinusBtn) bulkMinusBtn.disabled = state[sku] === 0;
      if (bulkPlusBtn) bulkPlusBtn.disabled = full;
    });

    const pct = totalPacks > 0 ? Math.min((selected / totalPacks) * 100, 100) : 0;
    const fillEl = portal.querySelector('#progress-fill');
    const selectedEl = portal.querySelector('#packs-selected');
    if (fillEl) fillEl.style.width = pct + '%';
    if (selectedEl) selectedEl.textContent = selected;

    portal.querySelectorAll('.upsell-stepper').forEach((stepper) => {
      const vid = String(stepper.getAttribute('data-variant-id') || '').trim();
      if (!vid) return;
      const qty = Number(upsells[vid] || 0);
      const countEl = stepper.querySelector('.upsell-count');
      const minus = stepper.querySelector('.upsell-minus');
      const plus = stepper.querySelector('.upsell-plus');
      if (countEl) countEl.textContent = String(qty);
      if (minus) minus.disabled = qty <= 0;
      if (plus) plus.disabled = qty >= MAX_UPSELL_QTY_PER_VARIANT;
    });

    portal.querySelectorAll('[data-shirt-addon-stepper]').forEach((stepper) => {
      const sizeEl = stepper.closest('.shirt-addon-row')?.querySelector('.js-shirt-addon-size');
      const size = String(sizeEl?.value || '').trim();
      const qty = addonShirtCount(size);
      const countEl = stepper.querySelector('.js-shirt-addon-count');
      const minus = stepper.querySelector('.js-shirt-addon-minus');
      const plus = stepper.querySelector('.js-shirt-addon-plus');
      if (countEl) countEl.textContent = String(qty);
      if (minus) minus.disabled = !size || qty <= 0;
      if (plus) plus.disabled = !size || shirtAddons.length >= MAX_ADDON_SHIRTS;
    });

    const confirmBtn = document.getElementById('pledge-confirm');
    const hintEl = document.getElementById('pledge-confirm-hint');
    const valid = isValid();
    if (confirmBtn) confirmBtn.disabled = !valid;
    if (hintEl) {
      if (valid) {
        hintEl.textContent = '';
      } else if (selected < totalPacks) {
        hintEl.textContent = `Choose ${totalPacks - selected} more pack${totalPacks - selected === 1 ? '' : 's'} to continue.`;
      } else if (!shirtSlotsFilled()) {
        const filled = (() => {
          let n = 0;
          for (let s = 1; s <= shirtEntitlementCount; s++) {
            if (portal.querySelector(`input[name="shirt_size_slot_${s}"]:checked`)) n++;
          }
          return n;
        })();
        const remaining = shirtEntitlementCount - filled;
        hintEl.textContent = `Choose a size for ${remaining} reward shirt${remaining === 1 ? '' : 's'} to continue.`;
      } else {
        hintEl.textContent = '';
      }
    }
  }

  function cartLineBackupItem(item) {
    const out = {
      id: item.variant_id,
      quantity: item.quantity,
    };
    if (item.properties && Object.keys(item.properties).length) out.properties = item.properties;
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      out.selling_plan = item.selling_plan_allocation.selling_plan.id;
    }
    return out;
  }

  async function readCartForRestore() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return [];
      const cart = await res.json();
      return Array.isArray(cart.items) ? cart.items.map(cartLineBackupItem).filter((item) => item.id && item.quantity > 0) : [];
    } catch (_) {
      return [];
    }
  }

  function cartLineHasPledgeMarker(item) {
    const p = item && item.properties ? item.properties : {};
    return (
      String(p._pledge_entitlement || '').trim() === '1' ||
      String(p._pledge_upsell || '').trim() === '1' ||
      String(p._pledge_swag || '').trim() === '1'
    );
  }

  async function readCartSnapshot() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return { items: [], restoreItems: [], regularItems: [] };
      const cart = await res.json();
      const items = Array.isArray(cart.items) ? cart.items : [];
      return {
        items,
        restoreItems: items.map(cartLineBackupItem).filter((item) => item.id && item.quantity > 0),
        regularItems: items.filter((item) => !cartLineHasPledgeMarker(item)),
      };
    } catch (_) {
      return { items: [], restoreItems: [], regularItems: [] };
    }
  }

  async function restoreCartItems(items) {
    if (!items.length) return;
    try {
      await fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' });
      await ZZAI.jsonPost('/cart/add.js', { items });
    } catch (restoreErr) {
      console.error('[zzai-pledge] cart restore failed:', restoreErr);
    }
  }

  function applyDefaultLargePledgeMix() {
    if (totalPacks !== 25 || selectedPacks() > 0) return;
    Object.assign(state, {
      mixed: 5,
      chapman: 5,
      zobo: 5,
      tamtam: 5,
      obi: 5,
    });
  }

  function addBulkPackControls() {
    if (totalPacks < 12) return;
    portal.querySelectorAll('.pack-qty').forEach((wrap) => {
      const plus = wrap.querySelector('.pack-plus');
      const minus = wrap.querySelector('.pack-minus');
      const sku = plus?.dataset?.sku || minus?.dataset?.sku || '';
      if (!sku || wrap.querySelector('.pack-bulk')) return;

      const minusFive = document.createElement('button');
      minusFive.type = 'button';
      minusFive.className = 'pack-bulk pack-bulk-minus';
      minusFive.dataset.sku = sku;
      minusFive.setAttribute('aria-label', 'Remove 5 packs');
      minusFive.textContent = '-5';
      minusFive.addEventListener('click', () => adjustPacks(sku, -5));

      const plusFive = document.createElement('button');
      plusFive.type = 'button';
      plusFive.className = 'pack-bulk pack-bulk-plus';
      plusFive.dataset.sku = sku;
      plusFive.setAttribute('aria-label', 'Add 5 packs');
      plusFive.textContent = '+5';
      plusFive.addEventListener('click', () => adjustPacks(sku, 5));

      if (minus) wrap.insertBefore(minusFive, minus);
      if (plus) wrap.appendChild(plusFive);
    });
  }

  /** Dev/theme preview only — preview data is not tied to a real signed-in backer. */
  function setPledgeCheckoutBusy(isBusy) {
    document.documentElement.dataset.zzaiPledgeCheckout = isBusy ? '1' : '';
    if (isBusy && typeof window.closeCartDrawer === 'function') {
      try {
        window.closeCartDrawer();
      } catch (_) {
        // Checkout redirect can continue even when the drawer is not mounted.
      }
    }
  }

  // Backer-chosen ship-by date: becomes the "Ship by" order attribute. The
  // app holds fulfillment until this date approaches, so the 3PL cannot ship
  // early. Window: 2 weeks to 6 months out; defaults to the earliest date.
  function shipByDateInput() {
    return document.getElementById('pledge-ship-by');
  }

  function isoDate(date) {
    return date.toISOString().slice(0, 10);
  }

  function initShipByDate() {
    const input = shipByDateInput();
    if (!input) return;
    const min = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    const max = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000);
    input.min = isoDate(min);
    input.max = isoDate(max);
    // No default: an untouched picker means "ship as soon as possible" and
    // the order is NOT held. Only an explicit choice delays shipping.
  }

  function shipByValue() {
    const input = shipByDateInput();
    if (!input) return '';
    const value = String(input.value || '').trim();
    if (!value) return '';
    if (input.min && value < input.min) return input.min;
    if (input.max && value > input.max) return input.max;
    return value;
  }

  initShipByDate();

  function pledgeCheckoutUrl() {
    const fromSection = String(portal.dataset.checkoutUrl || '').trim();
    const fromCore =
      window.ZZAI && typeof window.ZZAI.checkoutUrl === 'function' ? window.ZZAI.checkoutUrl() : '';
    const raw = fromSection || fromCore || '/checkout';
    try {
      const url = /^https?:\/\//i.test(raw)
        ? new URL(raw)
        : new URL(raw.startsWith('/') ? raw : `/${raw}`, window.location.origin);
      if (!/\/checkout\/?$/i.test(url.pathname)) return '/checkout';
      return url.href;
    } catch (_) {
      return '/checkout';
    }
  }

  function confirmPledgeCartReplacement(cartSnapshot) {
    const regularItems = cartSnapshot && Array.isArray(cartSnapshot.regularItems) ? cartSnapshot.regularItems : [];
    if (!regularItems.length) return Promise.resolve(true);

    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'pledge-cart-confirm';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.innerHTML = `
        <div class="pledge-cart-confirm__panel">
          <p class="pledge-cart-confirm__eyebrow">Separate checkout required</p>
          <h2>Start your pledge checkout?</h2>
          <p>Your current cart has ${regularItems.length} non-pledge item${regularItems.length === 1 ? '' : 's'}. To keep your Kickstarter reward secure, pledge rewards must checkout separately from regular store items.</p>
          <p class="pledge-cart-confirm__note">If checkout cannot open, we will restore your previous cart automatically.</p>
          <div class="pledge-cart-confirm__actions">
            <button type="button" class="pledge-cart-confirm__continue">Continue to pledge checkout</button>
            <button type="button" class="pledge-cart-confirm__cancel">Keep editing</button>
          </div>
        </div>
      `;

      const actions = overlay.querySelector('.pledge-cart-confirm__actions');
      const continueButton = overlay.querySelector('.pledge-cart-confirm__continue');
      const cancelButton = overlay.querySelector('.pledge-cart-confirm__cancel');
      if (actions) {
        actions.style.cssText = [
          'display: grid',
          'grid-template-columns: 1fr',
          'gap: 12px',
          'margin-top: 22px',
        ].join(';');
      }
      if (continueButton) {
        continueButton.style.cssText = [
          'display: inline-flex',
          'align-items: center',
          'justify-content: center',
          'width: 100%',
          'min-height: 48px',
          'border-radius: 999px',
          'border: 1px solid #111',
          'background: #111',
          'color: #fff',
          'font-weight: 800',
          'cursor: pointer',
        ].join(';');
      }
      if (cancelButton) {
        cancelButton.style.cssText = [
          'display: inline-flex',
          'align-items: center',
          'justify-content: center',
          'width: 100%',
          'min-height: 46px',
          'border-radius: 999px',
          'border: 1px solid #111',
          'background: transparent',
          'color: #111',
          'font-weight: 700',
          'cursor: pointer',
        ].join(';');
      }

      const done = (value) => {
        overlay.remove();
        resolve(value);
      };

      cancelButton?.addEventListener('click', () => done(false));
      continueButton?.addEventListener('click', () => done(true));
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) done(false);
      });
      overlay.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') done(false);
      });

      portal.appendChild(overlay);
      continueButton?.focus();
    });
  }

  function isPledgeThemePreview() {
    return (
      String(portal.dataset.ksBackerId || '').trim().toLowerCase() === 'preview' ||
      String(discountCode).trim().toUpperCase() === 'ZZAI-PREVIEW'
    );
  }

  function pledgeLineProps(sku, quantity) {
    return {
      _pledge_entitlement: '1',
      _pledge_sku: String(sku),
      _pledge_ks_id: String(ent.ksBackerId || ''),
      _pledge_locked_qty: String(Math.max(1, Number(quantity) || 1)),
    };
  }

  function entitlementShirtProps(slot, size) {
    return {
      _pledge_entitlement: '1',
      _pledge_item: 'shirt',
      _pledge_size: String(size),
      _pledge_shirt_slot: String(slot),
      _pledge_ks_id: String(ent.ksBackerId || ''),
      _pledge_locked_qty: '1',
    };
  }

  function swagLineProps(key) {
    return {
      _pledge_swag: '1',
      _pledge_swag_key: String(key),
      _pledge_ks_id: String(ent.ksBackerId || ''),
      _pledge_locked_qty: '1',
    };
  }

  function addonShirtProps(size) {
    return {
      _pledge_upsell: '1',
      _pledge_item: 'shirt',
      _pledge_size: String(size),
      _pledge_ks_id: String(ent.ksBackerId || ''),
    };
  }

  async function confirmSelection() {
    const confirmBtn = document.getElementById('pledge-confirm');
    if (!isValid() || !confirmBtn) return;

    if (isPledgeThemePreview()) {
      alert(
        'Theme preview cannot open checkout because it is not tied to a real signed-in backer. ' +
          'To test checkout, pledge reward credit, tax, and add-ons, open the pledge page while logged in as a pending test backer.',
      );
      return;
    }

    const items = [];
    let packSum = 0;

    for (const [sku, qty] of Object.entries(state)) {
      if (qty <= 0) continue;
      const rawId = VARIANT_IDS[sku];
      if (!variantAllowedForPack(rawId)) {
        alert('This page is out of date or was modified. Please refresh and try again.');
        return;
      }
      packSum += qty;
      items.push({
        id: rawId,
        quantity: qty,
        properties: pledgeLineProps(sku, qty),
      });
    }

    if (packSum !== totalPacks) {
      alert('Pack count does not match your pledge. Adjust quantities and try again.');
      return;
    }

    for (let slot = 1; slot <= shirtEntitlementCount; slot++) {
      const sizeRadio = portal.querySelector(`input[name="shirt_size_slot_${slot}"]:checked`);
      if (!sizeRadio) {
        alert('Select a size for each included reward shirt.');
        return;
      }
      const shirtRaw = SHIRT_VARIANT_IDS[sizeRadio.value];
      const sid = String(shirtRaw || '').trim();
      if (!sid || !allowShirt.has(sid)) {
        alert('Invalid shirt selection. Refresh the page or contact support.');
        return;
      }
      items.push({
        id: sid,
        quantity: 1,
        properties: entitlementShirtProps(slot, sizeRadio.value),
      });
    }

    for (const key of swagLineKeys) {
      const sid = String(swagVariantByKey[key] == null ? '' : swagVariantByKey[key]).trim();
      if (!sid || !/^\d+$/.test(sid)) {
        alert(
          supportContact
            ? 'A Kickstarter swag item is not linked in the theme. Contact us at ' + supportContact + '.'
            : 'A Kickstarter swag item is not linked in the theme. Contact the store for help.',
        );
        return;
      }
      if (!allowSwag.has(sid)) {
        alert('Invalid swag configuration. Refresh the page or contact support.');
        return;
      }
      items.push({
        id: sid,
        quantity: 1,
        properties: swagLineProps(key),
      });
    }

    Object.entries(upsells).forEach(([variantId, qtyRaw]) => {
      const vid = String(variantId || '').trim();
      const qty = Number(qtyRaw || 0);
      if (!vid || !allowUpsell.has(vid)) return;
      if (allowShirt.has(vid)) return;
      if (qty <= 0) return;
      items.push({
        id: vid,
        quantity: qty,
        properties: {
          _pledge_upsell: '1',
          _pledge_ks_id: String(ent.ksBackerId || ''),
        },
      });
    });

    shirtAddons.forEach((row) => {
      const vid = String(row.variantId || '').trim();
      if (!vid || !allowAddonShirt.has(vid)) return;
      items.push({
        id: vid,
        quantity: 1,
        properties: addonShirtProps(row.size),
      });
    });

    if (items.length === 0) {
      alert(
        supportContact
          ? 'No items could be added - please contact us at ' + supportContact + '.'
          : 'No items could be added - please contact the store for help.',
      );
      return;
    }

    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Processing...';
    setPledgeCheckoutBusy(true);

    const previousCart = await readCartSnapshot();
    const canReplaceCart = await confirmPledgeCartReplacement(previousCart);
    if (!canReplaceCart) {
      setPledgeCheckoutBusy(false);
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Confirm my selection';
      return;
    }

    try {
      await fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' });

      const addRes = await ZZAI.jsonPost('/cart/add.js', { items });
      if (!addRes.ok) {
        const body = await addRes.text();
        let msg;
        try {
          const parsed = JSON.parse(body);
          msg = parsed.description || parsed.message || body;
        } catch {
          msg = body;
        }
        if (addRes.status === 422 && String(msg).toLowerCase().includes('availab')) {
          throw new Error(
            'One or more items couldn\'t be added because stock is limited. ' +
            'A store admin needs to enable "Continue selling when out of stock" on each pack product in Shopify.'
          );
        }
        throw new Error(msg);
      }

      const attrs = {
        pledge_ks_id: String(ent.ksBackerId || ''),
        pledge_entitlement: '1',
        pledge_total_packs: String(totalPacks),
      };
      const shipBy = shipByValue();
      if (shipBy) attrs['Ship by'] = shipBy;
      const cartUpdateBody = {
        attributes: attrs,
        discount: '',
      };
      const updRes = await ZZAI.jsonPost('/cart/update.js', cartUpdateBody);
      if (!updRes.ok) throw new Error(await updRes.text());

      window.location.assign(pledgeCheckoutUrl());
    } catch (err) {
      console.error('[zzai-pledge] confirmSelection failed:', err);
      await restoreCartItems(previousCart.restoreItems);
      setPledgeCheckoutBusy(false);
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Confirm my selection ->';
      const raw = err && typeof err === 'object' && 'message' in err ? String(err.message || '') : '';
      const detail = raw.replace(/^Error:\s*/i, '').trim();
      const fallback = supportContact
        ? 'Something went wrong. Please try again or contact us at ' + supportContact + '.'
        : 'Something went wrong. Please try again or contact the store for help.';
      alert(detail || fallback);
    }
  }

  portal.querySelectorAll('.pack-plus').forEach((btn) => {
    btn.addEventListener('click', () => increment(btn.dataset.sku));
  });
  portal.querySelectorAll('.pack-minus').forEach((btn) => {
    btn.addEventListener('click', () => decrement(btn.dataset.sku));
  });

  portal.querySelectorAll('input[name^="shirt_size_slot_"]').forEach((radio) => {
    radio.addEventListener('change', updateUI);
  });

  portal.querySelectorAll('.upsell-plus').forEach((btn) => {
    btn.addEventListener('click', () => {
      const variantId = String(btn.dataset.variantId || '').trim();
      const current = Number(upsells[variantId] || 0);
      setUpsellQty(variantId, current + 1);
      updateUI();
    });
  });
  portal.querySelectorAll('.upsell-minus').forEach((btn) => {
    btn.addEventListener('click', () => {
      const variantId = String(btn.dataset.variantId || '').trim();
      const current = Number(upsells[variantId] || 0);
      setUpsellQty(variantId, current - 1);
      updateUI();
    });
  });

  const addonSize = portal.querySelector('.js-shirt-addon-size');
  const addonPlus = portal.querySelector('.js-shirt-addon-plus');
  const addonMinus = portal.querySelector('.js-shirt-addon-minus');
  if (addonSize) {
    updateAddonShirtPrice();
    addonSize.addEventListener('change', () => {
      updateAddonShirtPrice();
      updateUI();
    });
  }
  if (addonPlus && addonSize) {
    addonPlus.addEventListener('click', () => {
      if (addAddonShirtSize(addonSize.value)) {
        renderShirtAddonList();
        updateUI();
      }
    });
  }
  if (addonMinus && addonSize) {
    addonMinus.addEventListener('click', () => {
      if (removeAddonShirtSize(addonSize.value)) {
        renderShirtAddonList();
        updateUI();
      }
    });
  }

  const confirmBtn = document.getElementById('pledge-confirm');
  if (confirmBtn) confirmBtn.addEventListener('click', confirmSelection);

  applyDefaultLargePledgeMix();
  addBulkPackControls();
  renderShirtAddonList();
  updateUI();
})();
